import { useState } from 'react';
import { Loader2, FileDown, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useFinance } from '@/contexts/FinanceContext';
import { DREFilters } from './DREFilters';
import { DRETable } from './DRETable';
import { useDRE } from './useDRE';
import { exportDREPdf } from './dreExportPdf';
import { formatBRL, formatPeriodLabel } from './dreUtils';
import type { DREPeriodFilter } from './types';

export function DREPage() {
  const { client, clientId } = useCurrentClient();
  const { isLoading } = useFinance();
  const today = new Date();
  // Default: ano corrente, de janeiro até o mês atual (visão evolutiva do ano).
  const [filter, setFilter] = useState<DREPeriodFilter>({
    year: today.getFullYear(),
    fromMonth: 1,
    toMonth: today.getMonth() + 1,
  });

  const report = useDRE(filter);
  const fromDate = new Date(filter.year, report.fromMonth - 1, 1);
  const toDate = new Date(filter.year, report.toMonth, 0);

  if (!clientId) {
    return (
      <div>
        <h1 className="mb-1 font-brand text-2xl font-bold text-foreground">DRE</h1>
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <TrendingUp className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">Selecione um cliente no topo para ver a DRE.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleExport = () => {
    exportDREPdf({ report, clientName: client?.name ?? 'Cliente' });
  };

  const marginLabel =
    report.netVerticalPct === null
      ? ''
      : ` · margem ${report.netVerticalPct.toLocaleString('pt-BR', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })}%`;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">DRE</h1>
          <p className="text-sm text-muted-foreground">
            {formatPeriodLabel(fromDate, toDate)} · {client?.name ?? ''}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={isLoading}
          className="font-medium"
        >
          <FileDown className="mr-2 h-4 w-4" />
          Exportar PDF
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <DREFilters filter={filter} onChange={setFilter} />
        </CardContent>
      </Card>

      {/* Resumo do período em 3 cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Receitas
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-income">
              {formatBRL(report.income.total)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Despesas
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-expense">
              {formatBRL(report.expense.total)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Resultado{marginLabel}
            </p>
            <p
              className={`mt-1 text-2xl font-bold tabular-nums ${report.netTotal >= 0 ? 'text-income' : 'text-expense'}`}
            >
              {formatBRL(report.netTotal)}
            </p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : (
        <DRETable report={report} />
      )}

      <p className="text-xs text-muted-foreground">
        Valores em R$, por mês de competência (status <strong>Pago</strong>). A coluna{' '}
        <strong>%</strong> é a participação de cada linha sobre a receita total do período.
      </p>
    </div>
  );
}
